/**
 * Zero-config Vibeship DevTools component
 * Works immediately with no setup required
 */

"use client";

import React, { useState, useEffect } from 'react';
import { useClientSideScan, useProgressiveLevel } from './hooks';
import { TaskPanel } from './components/TaskPanel';
import { UpgradePrompt, UpgradeBanner } from './components/UpgradePrompt';
import { QuickSetupWizard } from './components/UpgradeWizard';
import { ErrorBoundary } from './components/ErrorBoundary';

export interface VibeshipDevToolsProps {
  /**
   * Position of the floating button
   */
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  
  /**
   * Whether to show upgrade prompts
   */
  showUpgradePrompts?: boolean;
  
  /**
   * Whether to start in open state
   */
  defaultOpen?: boolean;
  
  /**
   * Custom class name for the container
   */
  className?: string;
  
  /**
   * Callback when upgrade is initiated
   */
  onUpgrade?: (nextLevel: number) => void;
  
  /**
   * Scanner options (advanced)
   */
  scanOptions?: {
    includePublicFiles?: boolean;
    includeSourceMaps?: boolean;
    maxTasks?: number;
  };
}

export function VibeshipDevTools({
  position = 'bottom-left',
  showUpgradePrompts = true,
  defaultOpen = false,
  className = '',
  onUpgrade,
  scanOptions = {},
}: VibeshipDevToolsProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [dismissedUpgrade, setDismissedUpgrade] = useState(false);
  const [showSetupWizard, setShowSetupWizard] = useState(false);
  
  // Add keyboard shortcut support
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Cmd/Ctrl + Shift + K
      if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key === 'K') {
        event.preventDefault();
        setIsOpen(!isOpen);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);
  
  // Use the client-side scanner with conservative defaults
  const { tasks, isScanning, lastScanTime, rescan } = useClientSideScan({
    enabled: true,
    watchChanges: true,
    includePublicFiles: scanOptions.includePublicFiles ?? false, // Disabled by default to avoid 404s
    includeSourceMaps: scanOptions.includeSourceMaps ?? false,  // Disabled by default to avoid 404s
    maxTasks: scanOptions.maxTasks ?? 100,
  });
  
  // Use progressive level detection
  const { currentLevel, levels, canUpgrade } = useProgressiveLevel();
  
  // Show upgrade prompt after delay if applicable
  useEffect(() => {
    if (showUpgradePrompts && canUpgrade && !dismissedUpgrade && tasks.length > 0) {
      const timer = setTimeout(() => {
        setShowUpgrade(true);
      }, 30000); // Show after 30 seconds
      
      return () => clearTimeout(timer);
    }
  }, [showUpgradePrompts, canUpgrade, dismissedUpgrade, tasks.length]);
  
  // Position classes
  const positionClasses = {
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
  };
  
  const panelPositionClasses = {
    'bottom-right': 'bottom-16 right-4',
    'bottom-left': 'bottom-16 left-4',
    'top-right': 'top-16 right-4',
    'top-left': 'top-16 left-4',
  };
  
  return (
    <ErrorBoundary
      fallback={(error, retry) => (
        <div className={`fixed ${positionClasses[position]} z-50`}>
          <button
            onClick={retry}
            className="relative p-3 bg-red-500 text-white rounded-full shadow-lg hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            aria-label="DevTools Error - Click to retry"
            title={`DevTools Error: ${error.message}`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
        </div>
      )}
    >
      {/* Floating action button */}
      <div className={`fixed ${positionClasses[position]} z-50 ${className}`}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="relative p-3 bg-purple-500 text-white rounded-full shadow-lg hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          aria-label="Toggle DevTools"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
          </svg>
          
          {/* Task count badge */}
          {tasks.length > 0 && (
            <span className="absolute -top-1 -right-1 px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">
              {tasks.length}
            </span>
          )}
        </button>
      </div>
      
      {/* Task panel */}
      {isOpen && (
        <div className={`fixed ${panelPositionClasses[position]} z-40 w-96 h-[600px] max-h-[80vh] bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden`}>
          {/* Zero-config banner */}
          {currentLevel === 0 && !dismissedUpgrade && (
            <UpgradeBanner
              message="Running in read-only mode. Add an API route to enable editing."
              actionText="Setup"
              onAction={() => {
                setShowSetupWizard(true);
              }}
              onDismiss={() => setDismissedUpgrade(true)}
            />
          )}
          
          <TaskPanel
            tasks={tasks}
            isScanning={isScanning}
            lastScanTime={lastScanTime}
            onRescan={rescan}
            className="h-full"
          />
        </div>
      )}
      
      {/* Upgrade prompt */}
      {showUpgrade && currentLevel < levels.length - 1 && (
        <UpgradePrompt
          currentLevel={currentLevel}
          nextLevel={{
            ...levels[currentLevel + 1],
            setupTime: currentLevel === 0 ? '2 minutes' : '5 minutes',
            setupSteps: currentLevel === 0 ? [
              'Create /app/api/vibeship/route.ts',
              'Export handlers from @vibeship/devtools/api',
              'Restart your dev server',
            ] : [
              'Create vibeship.config.ts',
              'Define custom scan paths',
              'Enable additional features',
            ],
          }}
          onUpgrade={() => {
            onUpgrade?.(currentLevel + 1);
            setShowUpgrade(false);
            window.open('https://vibeship.dev/docs/upgrade', '_blank');
          }}
          onDismiss={() => {
            setShowUpgrade(false);
            setDismissedUpgrade(true);
          }}
          onLearnMore={() => {
            window.open('https://vibeship.dev/docs/features', '_blank');
          }}
        />
      )}
      
      {/* Setup Wizard */}
      {showSetupWizard && (
        <QuickSetupWizard
          onComplete={() => {
            setShowSetupWizard(false);
            onUpgrade?.(1);
            window.location.reload();
          }}
          onCancel={() => setShowSetupWizard(false)}
        />
      )}
    </ErrorBoundary>
  );
}

/**
 * Static DevTools panel for embedding in layouts
 */
export interface VibeshipDevToolsPanelProps {
  /**
   * Whether to show upgrade prompts
   */
  showUpgradePrompts?: boolean;
  
  /**
   * Custom class name for the container
   */
  className?: string;
  
  /**
   * Callback when upgrade is initiated
   */
  onUpgrade?: (nextLevel: number) => void;
  
  /**
   * Scanner options (advanced)
   */
  scanOptions?: {
    includePublicFiles?: boolean;
    includeSourceMaps?: boolean;
    maxTasks?: number;
  };
}

export function VibeshipDevToolsPanel({
  showUpgradePrompts = true,
  className = '',
  onUpgrade,
  scanOptions = {},
}: VibeshipDevToolsPanelProps) {
  const [dismissedUpgrade, setDismissedUpgrade] = useState(false);
  const [showSetupWizard, setShowSetupWizard] = useState(false);
  
  // Use the client-side scanner with conservative defaults
  const { tasks, isScanning, lastScanTime, rescan } = useClientSideScan({
    enabled: true,
    watchChanges: true,
    includePublicFiles: scanOptions.includePublicFiles ?? false, // Disabled by default to avoid 404s
    includeSourceMaps: scanOptions.includeSourceMaps ?? false,  // Disabled by default to avoid 404s
    maxTasks: scanOptions.maxTasks ?? 100,
  });
  
  // Use progressive level detection
  const { currentLevel, levels } = useProgressiveLevel();
  
  return (
    <div className={`w-full h-full ${className}`}>
      {/* Zero-config banner */}
      {showUpgradePrompts && currentLevel === 0 && !dismissedUpgrade && (
        <UpgradeBanner
          message="Running in read-only mode. Add an API route to enable editing."
          actionText="Setup"
          onAction={() => {
            onUpgrade?.(1);
            window.open('https://vibeship.dev/docs/setup', '_blank');
          }}
          onDismiss={() => setDismissedUpgrade(true)}
        />
      )}
      
      <TaskPanel
        tasks={tasks}
        isScanning={isScanning}
        lastScanTime={lastScanTime}
        onRescan={rescan}
        className="h-full"
      />
      
      {/* Setup Wizard */}
      {showSetupWizard && (
        <QuickSetupWizard
          onComplete={() => {
            setShowSetupWizard(false);
            onUpgrade?.(1);
            window.location.reload();
          }}
          onCancel={() => setShowSetupWizard(false)}
        />
      )}
    </div>
  );
}